import { NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OwlDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import {
  NbButtonModule,
  NbCardModule,
  NbDateService,
  NbDialogRef,
  NbIconModule,
  NbInputModule,
  NbOptionModule,
  NbSelectModule,
  NbToastrService,
  NbTooltipModule
} from '@nebular/theme';
import { Actions, Store, ofActionCompleted } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ManageInsight, SaveInsightComplete } from 'src/app/features/insights/redux';
import { InsightService } from 'src/app/features/insights/services/insight.service';
import { FREQUENCIES } from 'src/app/models/frequencies';
import { Insight } from 'src/app/models/insight';
import { RouterState } from '../../../../auth/redux';

@Component({
  selector: 'app-insight-status-handler',
  templateUrl: './insight-status-handler.component.html',
  imports: [
    NbCardModule,
    NgIf,
    NbIconModule,
    NbButtonModule,
    NbInputModule,
    FormsModule,
    OwlDateTimeModule,
    NbSelectModule,
    NgFor,
    NbOptionModule,
    NbTooltipModule
  ]
})
export class InsightStatusHandlerComponent implements OnInit, OnDestroy {
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe = new Subject<void>();

  public username = '';
  public action = '';
  public insight!: Insight;
  public FREQUENCIES = FREQUENCIES;
  public startDate?: Date;
  public endDate?: Date;
  public minStartDate: Date;
  public minEndDate: Date;

  constructor(
    protected ref: NbDialogRef<InsightStatusHandlerComponent>,
    private service: InsightService,
    private action$: Actions,
    private toastr: NbToastrService,
    protected dateService: NbDateService<Date>
  ) {
    this.minStartDate = this.dateService.today();
    this.minEndDate = this.dateService.addDay(this.dateService.today(), 1);
  }

  public ngOnInit() {
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.action$
      .pipe(ofActionCompleted(SaveInsightComplete), takeUntil(this.unsubscribe))
      .subscribe(() => this.close());
    this.action$.pipe(ofActionCompleted(ManageInsight), takeUntil(this.unsubscribe)).subscribe(() => this.close());

    if (this.insight.start_date) {
      this.startDate = new Date(this.insight.start_date * 1000);
      this.minEndDate = this.dateService.addDay(this.startDate, 1);
    }
    if (this.insight.end_date) {
      this.endDate = new Date(this.insight.end_date * 1000);
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public activate() {
    if (!this.insight.start_date || !this.insight.end_date) {
      this.toastr.danger('', $localize`Start and end times have to be provided to activate the Insight.`);
      return;
    }
    this.insight.enabled = true;
    this.insight.last_updated_by = this.username;
    this.insight.next_run_time = this.insight.start_date;
    this.service.postInsightsAmplify(this.insight, true);
  }

  public deactivate() {
    this.insight.last_updated_by = this.username;
    this.insight.enabled = false;
    this.insight.start_date = undefined;
    this.insight.end_date = undefined;
    this.insight.schedule_seconds = undefined;
    this.insight.next_run_time = undefined;
    this.service.postInsightsAmplify(this.insight, false);
  }

  public close() {
    this.ref.close();
  }

  public changeEvent() {
    if (this.startDate) {
      this.insight.start_date = Math.round(this.startDate.getTime() / 1000);
      if (this.endDate) {
        this.insight.end_date = Math.round(this.endDate.getTime() / 1000);
        if (this.startDate.getTime() > this.endDate.getTime()) {
          this.endDate = this.dateService.addDay(new Date(this.startDate.getTime()), 1);
        }
      } else {
        this.insight.end_date = undefined;
      }
      this.minEndDate = this.dateService.addDay(new Date(this.startDate.getTime()), 1);
    } else {
      this.insight.start_date = undefined;
    }
  }
}
